import { useState, useCallback, useMemo, useEffect } from 'react';
import { ChangeRequestCreateRequest, Member } from '@/api/bol/models';
import { getMembersApi } from '@/services/bolApiClient';
import { useTokenRoles } from '@/hooks';

interface MemberWithAccess extends Member {
  disabled?: boolean;
}

export const useChangeRequestMembers = (currentRevision: number) => {
  const { memberIdentifiers } = useTokenRoles();
  const [members, setMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedMemberIdentifier, setSelectedMemberIdentifier] = useState('');
  
  const [newChangeRequest, setNewChangeRequest] = useState<ChangeRequestCreateRequest>({
    logisticsObjectRevision: currentRevision,
    description: '',
    operations: []
  });
  
  const [operationsJsonText, setOperationsJsonText] = useState('[]');

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const membersApi = getMembersApi();
      const membersData = await membersApi.listMembers({ limit: 100, offset: 0 });
      setMembers(membersData.results);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const filteredMembers = useMemo<MemberWithAccess[]>(() => {
    if (!members.length || !memberIdentifiers?.length) {
      return members.map(member => ({ ...member, disabled: true }));
    }
    
    return members.map(member => {
      const hasAccess = memberIdentifiers.includes(member.identifier);
      return { ...member, disabled: !hasAccess };
    });
  }, [memberIdentifiers, members]);

  const handleMemberChange = useCallback((memberIdentifier: string) => {
    setSelectedMemberIdentifier(memberIdentifier);
    setNewChangeRequest(prev => ({
      ...prev,
      requestorMemberIdentifier: memberIdentifier || undefined
    }));
  }, []);

  const handleOperationsChange = useCallback((value: string) => {
    setOperationsJsonText(value);
    
    try {
      const operations = JSON.parse(value || '[]');
      if (Array.isArray(operations)) {
        setNewChangeRequest(prev => ({ ...prev, operations }));
      }
    } catch {
      // Invalid JSON, keep the text but don't update operations state
    }
  }, []);

  const resetForm = useCallback((revision: number) => {
    const enabledMembers = filteredMembers.filter(m => !m.disabled);
    const defaultMember = enabledMembers.length > 0 ? enabledMembers[0].identifier : '';
    
    const resetRequest = {
      logisticsObjectRevision: revision,
      requestorMemberIdentifier: defaultMember,
      description: '',
      operations: []
    };
    
    setNewChangeRequest(resetRequest);
    setSelectedMemberIdentifier(defaultMember);
    setOperationsJsonText(JSON.stringify(resetRequest.operations, null, 2));
  }, [filteredMembers]);

  return {
    members: filteredMembers,
    membersLoading,
    selectedMemberIdentifier,
    newChangeRequest,
    operationsJsonText,
    handleMemberChange,
    handleOperationsChange,
    fetchMembers,
    resetForm,
    setNewChangeRequest
  };
};
